import { useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { FormCreateWebhookData } from 'dashboard/forms/FormCreateWebhook/FormCreateWebhook';
import { FormPasswordConfirmationModalData } from 'dashboard/forms/FormPasswordConfirmationModal';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Webhook } from 'dashboard/models/webhook';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadWebhookList } from 'dashboard/redux/thunk-actions/webhooks';
import DashboardWebhooksView from 'dashboard/views/DashboardWebhooksView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardWebhooksRoute = (props: PropsFromRedux) => {
  const navigate = useNavigate();

  const abortController = useMemo(
    () => new AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.loadWebhookList, props.currentEnvironment],
  );

  const { loading: isLoadingWebhooks, callback: loadWebhooks } = useApiLoader({
    loader: () =>
      props.loadWebhookList({
        currentEnvironment: props.currentEnvironment,
        abortSignal: abortController.signal,
      }),
    deps: [props.currentEnvironment],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to load webhooks, please reload to try again',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      );
    },
  });

  // ==============================
  // Create Webhook
  // ==============================
  const { error: webhookCreateError, callback: handleCreateWebhook } = useApiCallback(
    async (data: FormCreateWebhookData, meta?: FormPasswordConfirmationModalData) => {
      const response = await SessionClientDashboard.postResourceCreate<Webhook>(
        {
          type: ApiModel.Webhook,
          attributes: {
            'environment-id': props.currentEnvironment.id,
            ...data,
          },
        },
        {
          meta: {
            // Note this will be blank if organization has SAML enabled.
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'password-confirmation': meta?.['password-confirmation'],
          },
        },
      );

      loadWebhooks();
      props.showMessage('Successfully created webhook', MessageType.Success);
      navigate(`/dashboard/webhooks/${response.data.id}`);
    },
    [props.currentEnvironment],
  );

  useApiErrorHandler({
    error: webhookCreateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        const details = error.errors?.[0]?.details;
        const errorMessage: string =
          typeof details === 'string' ? details : 'Failed to create webhook, please try again';
        props.showMessage(errorMessage, MessageType.Failure, false);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to create webhook, please try again', MessageType.Failure, false),
  });

  return (
    <DashboardWebhooksView
      organization={props.organization}
      webhooks={props.webhooks}
      isLoadingWebhooks={isLoadingWebhooks}
      handleCreateWebhook={handleCreateWebhook}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    organization: state.organization.organization,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    webhooks: state.webhooks.webhooks,
  };
};

const mapDispatchToProps = {
  loadWebhookList,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardWebhooksRoute);
